import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { api } from "../utils/api";
import LanguageSwitcher from "./LanguageSwitcher";
import ReEngagementBanner from "./ReEngagementBanner";
import AdBanner from "./AdBanner";

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "")
  .split(",").map(e => e.trim().toLowerCase()).filter(Boolean);

function isAdmin(user) {
  return !!(user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase()));
}

/* ─── icônes SVG inline (pas d'emoji, rendu propre) ─── */
const Icon = {
  feed:      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm0 8a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2z"/></svg>,
  directory: <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM2 8a2 2 0 114 0 2 2 0 01-4 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906zM10 12a5 5 0 015 5v1H5v-1a5 5 0 015-5z"/></svg>,
  encounters:<svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/></svg>,
  places:    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>,
  messages:  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/><path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/></svg>,
  blog:      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm2-1h12a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1zM5 7a1 1 0 000 2h10a1 1 0 100-2H5zm0 4a1 1 0 100 2h6a1 1 0 100-2H5z" clipRule="evenodd"/></svg>,
  visitors:  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>,
  premium:   <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>,
  pro:       <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd"/></svg>,
  profile:   <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/></svg>,
  admin:     <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/></svg>,
  logout:    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm7 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>,
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [completeness, setCompleteness] = useState(null);
  const admin = isAdmin(user);

  /* nav desktop : 5 liens principaux visibles, reste dans le menu avatar */
  const MAIN_NAV = [
    { to: "/", label: t("nav_feed") || "Fil", icon: Icon.feed, end: true },
    { to: "/directory", label: t("nav_directory") || "Annuaire", icon: Icon.directory },
    { to: "/encounters", label: t("nav_encounters") || "Rencontres", icon: Icon.encounters },
    { to: "/places", label: t("nav_places") || "Lieux", icon: Icon.places },
    { to: "/messages", label: t("nav_messages") || "Messages", icon: Icon.messages, badge: unreadCount },
    { to: "/blog", label: t("nav_blog") || "Blog", icon: Icon.blog },
  ];

  /* menu déroulant avatar */
  const AVATAR_MENU = [
    { to: "/visitors", label: t("nav_visitors") || "Visiteurs", icon: Icon.visitors },
    { to: "/premium", label: t("nav_premium") || "Premium", icon: Icon.premium },
    { to: "/pro", label: t("nav_pro") || "Pro", icon: Icon.pro },
    { to: "/profile", label: t("nav_profile") || "Profil", icon: Icon.profile },
    ...(admin ? [{ to: "/admin", label: "Admin", icon: Icon.admin, isAdmin: true }] : []),
  ];

  const fetchUnread = useCallback(async () => {
    try {
      const data = await api("/api/messages/conversations");
      const total = (data.conversations || []).reduce((s, c) => s + (c.unread || 0), 0);
      setUnreadCount(total);
    } catch {}
  }, []);

  const fetchCompleteness = useCallback(async () => {
    if (!user) return;
    try {
      const data = await api("/api/profiles/completeness");
      setCompleteness(data);
      if (data.score < 80) setShowBanner(true);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  useEffect(() => { fetchCompleteness(); }, [fetchCompleteness]);
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const initials = (user?.username || user?.email || "?")[0].toUpperCase();

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#0e0e18] pb-20 sm:pb-0">

      {/* ══════════════════════════════════════════════════════
          HEADER — logo large + nav épurée + avatar menu
         ══════════════════════════════════════════════════════ */}
      <header className="sticky top-0 z-40 bg-[#0e0e18]/95 backdrop-blur-xl"
        style={{ borderBottom: "1px solid rgba(139,111,245,0.12)" }}>
        <div className="mx-auto flex h-16 max-w-screen-xl items-center gap-2 px-4 sm:px-8">

          {/* ── Logo seul, grand ── */}
          <NavLink to="/" className="mr-8 shrink-0">
            <img src="/logo.png" alt="Univers Libertin" className="h-10 w-auto" />
          </NavLink>

          {/* ── Nav principale desktop ── */}
          <nav className="hidden flex-1 items-center gap-1 sm:flex">
            {MAIN_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `relative flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? "bg-[#8b6ff5]/15 text-[#c4b5fd]"
                      : "text-white/40 hover:bg-white/[0.04] hover:text-white/75"
                  }`
                }
              >
                {item.icon}
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* ── Droite : langue + avatar ── */}
          <div className="ml-auto flex items-center gap-3">
            <LanguageSwitcher compact />

            {/* Avatar + dropdown */}
            <div className="relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white transition-all"
                style={{
                  background: "linear-gradient(135deg,#7c3aed,#5b21b6)",
                  boxShadow: mobileMenuOpen ? "0 0 0 2px #8b6ff5" : "0 0 0 2px rgba(139,111,245,0.2)",
                }}
              >
                {initials}
              </button>

              {mobileMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMobileMenuOpen(false)} />
                  <div
                    className="absolute right-0 top-11 z-50 w-52 overflow-hidden rounded-xl py-1 shadow-2xl"
                    style={{
                      background: "#1a1a2e",
                      border: "1px solid rgba(139,111,245,0.18)",
                    }}
                  >
                    {/* Infos user */}
                    <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <p className="text-[13px] font-semibold text-white/80">{user?.username || "Mon profil"}</p>
                      <p className="truncate text-[11px] text-white/30">{user?.email}</p>
                    </div>

                    {AVATAR_MENU.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium transition-colors ${
                            item.isAdmin
                              ? "text-amber-400/70 hover:bg-amber-500/8 hover:text-amber-300"
                              : isActive
                                ? "bg-[#8b6ff5]/10 text-[#c4b5fd]"
                                : "text-white/50 hover:bg-white/[0.04] hover:text-white/85"
                          }`
                        }
                      >
                        <span className={item.isAdmin ? "text-amber-400" : "text-white/30"}>{item.icon}</span>
                        {item.label}
                      </NavLink>
                    ))}

                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                      <button
                        onClick={() => { setMobileMenuOpen(false); logout(); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-rose-400/60 transition-colors hover:bg-rose-500/8 hover:text-rose-400"
                      >
                        {Icon.logout}
                        {t("nav_logout") || "Déconnexion"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════
          BANNER
         ══════════════════════════════════════════════════════ */}
      {showBanner && completeness && completeness.score < 80 && (
        <div className="mx-auto max-w-screen-xl px-5 pt-4 sm:px-8">
          <ReEngagementBanner completeness={completeness} onDismiss={() => setShowBanner(false)} />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          CONTENU — layout 2 colonnes sur grands écrans
         ══════════════════════════════════════════════════════ */}
      <div className="mx-auto w-full max-w-screen-xl px-4 py-6 sm:px-8">
        <div className="flex gap-6">
          {/* Colonne principale */}
          <main className="min-w-0 flex-1">
            <Outlet />
          </main>

          {/* Sidebar desktop avec encarts pub */}
          <aside className="hidden shrink-0 xl:block" style={{ width: "280px" }}>
            <div className="sticky top-24 space-y-4">
              <AdBanner variant="square" showLabel />
              {/* 2e encart si jamais il y en a plusieurs */}
              <AdBanner variant="square" />
            </div>
          </aside>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          FOOTER — fond légèrement différencié, typo lisible
         ══════════════════════════════════════════════════════ */}
      <footer className="hidden sm:block" style={{ background: "#09090f", borderTop: "1px solid rgba(139,111,245,0.1)" }}>
        <div className="mx-auto max-w-screen-xl px-8 py-12">
          <div className="grid grid-cols-4 gap-8">

            {/* Col 1 : logo seul */}
            <div>
              <Link to="/" className="mb-4 block">
                <img src="/logo.png" alt="Univers Libertin" className="h-12 w-auto" />
              </Link>
              <p className="text-[12px] leading-relaxed" style={{ color: "rgba(255,255,255,0.35)" }}>
                La communauté libertine de référence. Rencontres, lieux et événements en toute discrétion.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {["🔒 Modéré", "🛡 Discret", "💳 Stripe"].map(b => (
                  <span key={b}
                    className="rounded-full px-2.5 py-1 text-[10px]"
                    style={{ background: "rgba(139,111,245,0.08)", color: "rgba(196,181,253,0.6)", border: "1px solid rgba(139,111,245,0.15)" }}>
                    {b}
                  </span>
                ))}
              </div>
            </div>

            {/* Col 2 : Découvrir */}
            <div>
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-widest"
                style={{ color: "#8b6ff5" }}>
                Découvrir
              </h4>
              {[
                ["/p/a-propos", "À propos"],
                ["/p/comment-ca-marche", "Comment ça marche"],
                ["/p/annuaire-clubs-libertins-france", "Annuaire des clubs"],
                ["/p/rencontres-libertines", "Rencontres"],
                ["/p/tarifs", "Tarifs"],
              ].map(([to, label]) => (
                <Link key={to} to={to}
                  className="mb-2.5 block text-[13px] transition-colors hover:text-white/80"
                  style={{ color: "rgba(255,255,255,0.45)" }}>
                  {label}
                </Link>
              ))}
            </div>

            {/* Col 3 : Légal */}
            <div>
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-widest"
                style={{ color: "#8b6ff5" }}>
                Légal
              </h4>
              {[
                ["/p/mentions-legales", "Mentions légales"],
                ["/p/politique-de-confidentialite", "Confidentialité"],
                ["/p/conditions-generales-utilisation", "CGU"],
                ["/p/charte-communautaire", "Charte communautaire"],
              ].map(([to, label]) => (
                <Link key={to} to={to}
                  className="mb-2.5 block text-[13px] transition-colors hover:text-white/80"
                  style={{ color: "rgba(255,255,255,0.45)" }}>
                  {label}
                </Link>
              ))}
            </div>

            {/* Col 4 : Contact */}
            <div>
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-widest"
                style={{ color: "#8b6ff5" }}>
                Contact
              </h4>
              {[
                ["/p/contact", "Nous contacter"],
                ["/p/securite-confidentialite", "Sécurité"],
              ].map(([to, label]) => (
                <Link key={to} to={to}
                  className="mb-2.5 block text-[13px] transition-colors hover:text-white/80"
                  style={{ color: "rgba(255,255,255,0.45)" }}>
                  {label}
                </Link>
              ))}
              <a href="mailto:contact@universlibertin.fr"
                className="mt-3 block text-[12px] transition-colors hover:text-[#c4b5fd]"
                style={{ color: "rgba(139,111,245,0.6)" }}>
                contact@universlibertin.fr
              </a>
            </div>
          </div>

          {/* Encart pub dans le footer */}
          <div className="mt-8">
            <AdBanner variant="horizontal" showLabel />
          </div>

          {/* Bas de footer */}
          <div className="mt-6 flex items-center justify-between pt-6"
            style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.2)" }}>
              © {new Date().getFullYear()} Univers Libertin — TRUSTOVA SAS — Tous droits réservés
            </p>
            <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.15)" }}>
              Réservé aux adultes · +18
            </p>
          </div>
        </div>
      </footer>

      {/* ══════════════════════════════════════════════════════
          BARRE MOBILE (5 onglets fixes)
         ══════════════════════════════════════════════════════ */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 sm:hidden"
        style={{ background: "rgba(14,14,24,0.97)", borderTop: "1px solid rgba(139,111,245,0.12)", backdropFilter: "blur(20px)" }}>
        <div className="flex">
          {MAIN_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `relative flex flex-1 flex-col items-center justify-center gap-1 py-3 transition-colors ${
                  isActive ? "text-[#c4b5fd]" : "text-white/25"
                }`
              }
            >
              <span className="h-5 w-5">{item.icon}</span>
              <span className="text-[9px] font-medium leading-none">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute right-[22%] top-1.5 flex h-4 min-w-[14px] items-center justify-center rounded-full bg-rose-500 px-1 text-[8px] font-bold text-white">
                  {item.badge > 9 ? "9+" : item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
